"use client"

import { useRef, useState } from "react"
import { BellRing, Check } from "lucide-react"

/**
 * Footer newsletter band (chrome-upgrade zone 6, owner redesign 07/20/2026).
 *
 * A deliberately composed band within the footer — not an afterthought form:
 * two-column at desktop (value proposition left, form right), clean stacked
 * composition at mobile, subtle brand-tinted background, ORANGE primary CTA.
 * Copy aligned with the owner-approved capture-popup language: price-drop +
 * recall value, honest, no hype.
 *
 * Mechanics unchanged: double-opt-in via the EXISTING subscribe machinery
 * (POST /api/alerts/subscribe, type 'newsletter'); confirmation email gates
 * delivery; honeypot-guarded; optional first/last name personalize the
 * confirm greeting and are never required.
 */
type SubmitStatus = "idle" | "submitting" | "success" | "error"

const FIELD_CLASS =
  "w-full min-w-0 rounded-full border border-gray-300 bg-white px-5 h-12 text-[15px] text-gray-900 placeholder:text-gray-500 focus:border-[#ff9951] focus:outline-none focus:ring-2 focus:ring-[#ff9951]/25 transition-colors"

export function FooterNewsletter() {
  const [status, setStatus] = useState<SubmitStatus>("idle")
  const [email, setEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const honeypotRef = useRef("")

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (honeypotRef.current) return
    setStatus("submitting")
    try {
      const res = await fetch("/api/alerts/subscribe", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          type: "newsletter",
          email,
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
          generalUpdatesOptIn: true,
          source: "footer",
          honeypot: honeypotRef.current,
        }),
      })
      const data = await res.json().catch(() => null)
      if (res.ok && data?.ok) setStatus("success")
      else setStatus("error")
    } catch {
      setStatus("error")
    }
  }

  return (
    <div className="rounded-2xl bg-gradient-to-br from-[#0469f0]/[0.05] via-white to-[#ff9951]/[0.07] ring-1 ring-gray-200 px-6 py-8 sm:px-10 sm:py-10">
      <div className="grid gap-8 lg:grid-cols-2 lg:gap-14 items-center">
        {/* Value proposition */}
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-[#ff9951]/10 px-3.5 py-1.5 mb-4">
            <BellRing className="w-3.5 h-3.5 text-[#ff9951]" aria-hidden="true" />
            <span className="text-xs font-semibold uppercase tracking-wider text-[#ff9951]">
              Price &amp; recall alerts
            </span>
          </div>
          <h2 className="text-2xl sm:text-[28px] font-black tracking-[-0.02em] text-gray-900 leading-tight">
            Never overpay for a fridge or freezer.
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-gray-600 max-w-md">
            One email when we spot a major refrigerator price drop or a safety
            recall — nothing else. Unsubscribe anytime.
          </p>
        </div>

        {/* Form */}
        <div>
          {status === "success" ? (
            <div data-state="success" className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#10a65c]/10">
                <Check className="w-5 h-5 text-[#10a65c]" aria-hidden="true" />
              </span>
              <div>
                <p className="text-[15px] font-semibold text-gray-900">
                  Check your inbox to confirm.
                </p>
                <p className="mt-1 text-sm text-gray-600">
                  We just emailed you a confirmation link. Click it and
                  you&apos;re set — nothing arrives until you confirm.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit}>
              {/* Honeypot: hidden field real users never fill. */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
                onChange={(e) => {
                  honeypotRef.current = e.target.value
                }}
              />
              {/* Optional first/last name — personalize the confirm email
                  greeting; never required, never a reason to reject. */}
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name (optional)"
                  autoComplete="given-name"
                  maxLength={60}
                  disabled={status === "submitting"}
                  aria-label="First name (optional)"
                  className={FIELD_CLASS + " sm:flex-1"}
                />
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name (optional)"
                  autoComplete="family-name"
                  maxLength={60}
                  disabled={status === "submitting"}
                  aria-label="Last name (optional)"
                  className={FIELD_CLASS + " sm:flex-1"}
                />
              </div>
              <label htmlFor="footer-nl-email" className="sr-only">
                Email address
              </label>
              <div className="mt-3 flex flex-col sm:flex-row gap-3">
                <input
                  id="footer-nl-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  autoComplete="email"
                  disabled={status === "submitting"}
                  className={FIELD_CLASS + " sm:flex-1"}
                />
                {/* Owner rule: primary buttons are brand ORANGE. */}
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="h-12 shrink-0 rounded-full bg-[#ff9951] hover:bg-[#ff8533] disabled:opacity-100 px-8 text-[15px] font-bold text-[var(--color-brand-action-text)] whitespace-nowrap transition-colors shadow-sm disabled:cursor-not-allowed"
                >
                  {status === "submitting" ? "Sending…" : "Get the alerts"}
                </button>
              </div>
              {/* FRIDGE-149 §5 — role="alert" so the failure is announced the
                  moment it renders (same pattern as the price-watch form). */}
              {status === "error" && (
                <p role="alert" className="mt-2.5 text-xs text-red-600">
                  Something went wrong. Please check your email and try again.
                </p>
              )}
              <p className="mt-2.5 text-xs text-gray-500">
                We respect your privacy. No spam, ever — unsubscribe anytime.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
